<template>
  <div id="app">
    <notifications group="app" position="bottom right"/>
    <app-overlay v-if="$store.getters['getOverlay']"></app-overlay>

    <app-header v-if="$store.getters['getHeader']"></app-header>

    <app-integration-expired-banner></app-integration-expired-banner>

    <app-trial-banner></app-trial-banner>

    <router-view/>

    <app-footer v-if="$store.getters['getFooter']"></app-footer>
  </div>
</template>

<script>
import axios from 'axios'
  import Header from './components/common/header/header.vue'
  import Footer from './components/common/footer/footer.vue'
  import TrialBanner from './components/common/TrialBanner.vue'
  import IntegrationExpiredBanner from './components/common/IntegrationExpiredBanner.vue'
  import { store } from './store/store'
  import { loadCompanyIntegrations } from './helpers/companyIntegrations'
  import { resetAppScroll } from './helpers/scrollToTop'
  import { watchSystemTheme } from './helpers/theme'

  export default {
    name: 'app',
    components: {
      'app-header': Header,
      'app-footer': Footer,
      'app-trial-banner': TrialBanner,
      'app-integration-expired-banner': IntegrationExpiredBanner
    },
    data: function () {
      return {
        _unwatchTheme: null
      }
    },
    methods: {
      loadCompanyIntegrations: function () {
        if (!this.$store.getters['user/isAuthenticated']) return Promise.resolve()
        return loadCompanyIntegrations(this.$store)
      },
      getCategories: function () {
        axios.get('/api/categories').then(response => {
          const flat = response.data.flat_categories || []
          const hierarchical = response.data.categories || []
          this.$store.commit('category/setAll', flat)
          this.$store.commit('category/setCategories', hierarchical.length ? hierarchical : flat.filter(c => c.parent_id === null || c.parent_id === 0))
        }).catch(() => {
          this.$store.commit('category/setAll', [])
          this.$store.commit('category/setCategories', [])
        })
      },
      getCompany: function () {
        // Commented out until backend route is implemented
        // axios.get('/api/company/' + this.$store.getters['user/getProfile'].company_id).then(response => {
        //   this.$store.commit('company/setProfile', response.data.company)
        //   this.$store.commit('company/setIntegrations', response.data.integrations)
        // })
      },
      getAccountSettings: function () {
        // Commented out until backend route is implemented
        // axios.get('/api/setting/account').then(response => {
        //   this.$store.commit('setting/setAccountSettings', response.data)
        // })
      },
      getDisplaySettings: function () {
        // Commented out until backend route is implemented
        // axios.get('/api/setting/display').then(response => {
        //   this.$store.commit('setting/setDisplaySettings', response.data)
        // })
      }
    },
    created: function () {
      this._unwatchTheme = watchSystemTheme()
      if (this.$store && this.$store.getters && this.$store.getters['user/isAuthenticated']) {
        this.loadCompanyIntegrations()
      }
    },
    beforeUnmount: function () {
      if (typeof this._unwatchTheme === 'function') {
        this._unwatchTheme()
        this._unwatchTheme = null
      }
    },
    watch: {
      '$route.fullPath': function () {
        resetAppScroll()
      }
    },
    updated: function () {
      // Check if store and user profile are available before calling methods
      if (this.$store && this.$store.getters && this.$store.getters['user/getProfile']) {
        this.getCategories()
        this.getCompany()
        this.getAccountSettings()
        this.getDisplaySettings()
      }
    }
  }
</script>

<style>
  @import '~semantic-ui-css/semantic.min.css';
  @import '~sweetalert2/dist/sweetalert2.min.css';
  @import '~vue-form-wizard/dist/vue-form-wizard.min.css';
  @import '~vue-select/dist/vue-select.css';
  @import 'assets/css/tokens.css';
  @import 'assets/css/app.css';
  @import 'assets/css/modal.css';
  @import 'assets/css/document-preview.css';
  @import 'assets/css/compare.css';
  @import 'assets/css/task-modal.css';
  @import 'assets/css/select.css';
  @import 'assets/css/form-controls.css';
  @import "assets/css/checkbox.css";
  @import 'assets/css/theme-surfaces.css';
  @import 'assets/css/document-list-table.css';

  body.no-scroll {
    overflow: hidden;
  }

  /*
   * SweetAlert popups stay light. Global h2 { color: var(--color-text) } makes
   * titles white-on-white in dark mode — pin readable charcoal for title/body.
   */
  .swal2-popup {
    background: #FFFFFF !important;
    color: #545454 !important;
  }
  .swal2-popup .swal2-title,
  .swal2-popup h2.swal2-title {
    color: #595959 !important;
  }
  .swal2-popup .swal2-content,
  .swal2-popup .swal2-html-container,
  .swal2-popup .swal2-content p,
  .swal2-popup .swal2-html-container p {
    color: #545454 !important;
  }

  /*
   * SweetAlert confirmation cancels use the Storybook neutral Basic Button
   * treatment. Popup stays light — pin light-mode label/border so dark theme
   * tokens (--color-text-muted: #CED0DA) do not wash out the label.
   */
  .swal2-cancel.swal2-styled {
    background: transparent none !important;
    color: #707070 !important;
    box-shadow: 0 0 0 1px #CED0DA inset !important;
    font-weight: 700;
  }
  .swal2-cancel.swal2-styled:hover {
    background-color: rgba(0, 0, 0, 0.05) !important;
    color: rgba(0, 0, 0, 0.8) !important;
    box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.35) inset !important;
  }
  .swal2-cancel.swal2-styled:focus {
    background-color: transparent !important;
    color: #707070 !important;
    box-shadow:
      0 0 0 1px rgba(34, 36, 38, 0.35) inset,
      0 0 0 3px rgba(34, 36, 38, 0.12) !important;
  }

  /*
   * Confirmation warnings use Smardie's info asset in destructive red rather
   * than SweetAlert's thin orange warning glyph.
   */
  .swal2-popup:not(.swal2-toast) .swal2-icon.swal2-warning {
    width: 5em;
    height: 5em;
    border: 0 !important;
    border-radius: 0;
    background-color: #FF4C4C;
    color: transparent !important;
    -webkit-mask: url('./assets/img/icn_info.svg') center / contain no-repeat;
    mask: url('./assets/img/icn_info.svg') center / contain no-repeat;
  }
  .swal2-popup:not(.swal2-toast) .swal2-warning .swal2-icon-text {
    display: none;
  }

  /*
   * Destructive confirmation CTAs (visible warning icon only).
   * SweetAlert keeps every icon type in the popup DOM and toggles display,
   * so bare :has(.swal2-success)/:has(.swal2-warning) matches ALL modals.
   */
  .swal2-confirm.swal2-confirm-destructive,
  .swal2-popup:not(.swal2-toast):has(.swal2-icon.swal2-warning[style*="display: flex"]) .swal2-confirm.swal2-styled {
    background-color: #FF4C4C !important;
    background-image: none !important;
    color: #FFFFFF !important;
    border-color: #FF4C4C !important;
  }
  .swal2-confirm.swal2-confirm-destructive:hover,
  .swal2-popup:not(.swal2-toast):has(.swal2-icon.swal2-warning[style*="display: flex"]) .swal2-confirm.swal2-styled:hover {
    background-color: var(--color-error) !important;
    background-image: none !important;
    color: #FFFFFF !important;
  }
  .swal2-confirm.swal2-confirm-destructive:focus,
  .swal2-popup:not(.swal2-toast):has(.swal2-icon.swal2-warning[style*="display: flex"]) .swal2-confirm.swal2-styled:focus {
    box-shadow:
      0 0 0 2px #FFFFFF,
      0 0 0 4px rgba(255, 76, 76, 0.4) !important;
  }

  /*
   * Success dialogs use the shared green outlined-check asset instead of
   * SweetAlert's pale animated tick.
   */
  .swal2-popup:not(.swal2-toast) .swal2-icon.swal2-success {
    width: 5em;
    height: 5em;
    border: 0 !important;
    border-radius: 0;
    background: url('./assets/img/icn_success_outline.svg') center / contain no-repeat;
  }
  .swal2-popup:not(.swal2-toast) .swal2-icon.swal2-success > * {
    display: none !important;
  }

  /* Storybook Primary Button treatment for success-dialog confirmation. */
  .swal2-popup:not(.swal2-toast):has(.swal2-icon.swal2-success[style*="display: flex"]) .swal2-confirm.swal2-styled {
    background-color: #4DA1FF !important;
    color: #FFFFFF !important;
    font-family: var(--font-regular);
    box-shadow: none !important;
  }
  .swal2-popup:not(.swal2-toast):has(.swal2-icon.swal2-success[style*="display: flex"]) .swal2-confirm.swal2-styled:hover {
    background-color: var(--color-primary) !important;
  }
  .swal2-popup:not(.swal2-toast):has(.swal2-icon.swal2-success[style*="display: flex"]) .swal2-confirm.swal2-styled:focus {
    box-shadow:
      0 0 0 2px #FFFFFF,
      0 0 0 4px rgba(77, 161, 255, 0.4) !important;
  }

  input.error,
  select.error,
  .select.error,
  .select.error select {
    background-color: var(--color-very-light-grey) !important;
    border-color: var(--color-border) !important;
    color: var(--color-error) !important;
    -webkit-box-shadow: none;
    box-shadow: none;
  }

  .validation-errors ul li {
    color: var(--color-error) !important;
  }

  /*
   * Vuejs wizard overrides
   */
  .vue-form-wizard {
    margin-top: -25px;
    padding-bottom: 0 !important;
  }
  .wizard-nav {
    padding-bottom: 30px;
  }
  .wizard-tab-content {
    padding: 0 !important;
  }
  .wizard-header {
    display: none !important;
  }

  /*
   * vselect overrides
   */
  .v-select input[type=search] {
    padding: 5px 7px !important;
  }
  .v-select .dropdown-toggle .clear {
    visibility: hidden !important;
  }

  /*
   * Add margin to form error message when in a modal window
   */
  .modal-wrapper .ui.error.message {
    margin: 25px;
  }

  /*
   * Product image slider overrides
   */
  .product-slider img {
    width: 100%;
    height: auto;
    border-radius: 4px 4px 0 0;
  }
</style>
